import React from "react";

import {
  BounceSpinner,
  FoldSpinner,
  LoadingText,
  PulseSpinner,
  RingSpinner,
  RotateSpinner,
  SpinnerWrapper,
} from "./styled";
import type { LoadingSpinnerProps } from "./types";

/**
 * A creative and customizable loading spinner component with multiple animation variants
 *
 * @example
 * ```tsx
 * // Basic usage
 * <LoadingSpinner isLoading={true} />
 *
 * // With custom variant and size
 * <LoadingSpinner
 *   isLoading={true}
 *   variant="pulse"
 *   size="large"
 *   color="primary"
 * />
 *
 * // With loading text
 * <LoadingSpinner
 *   isLoading={true}
 *   variant="ring"
 *   showText={true}
 *   text="Loading data..."
 * />
 *
 * // Full screen overlay (fixed position)
 * <LoadingSpinner
 *   isLoading={true}
 *   variant="fold"
 *   overlay={true}
 *   overlayMode="fixed"
 *   centered={true}
 * />
 *
 * // Content overlay (absolute position)
 * <LoadingSpinner
 *   isLoading={true}
 *   variant="ring"
 *   overlay={true}
 *   overlayMode="absolute"
 *   centered={true}
 *   overlayColor="rgba(255, 255, 255, 0.9)"
 * />
 * ```
 */
export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  isLoading = true,
  variant = "rotate",
  size = "medium",
  color = "primary",
  speed = "normal",
  width,
  height,
  showText = false,
  text = "Loading...",
  centered = false,
  overlay = false,
  overlayMode = "fixed",
  overlayColor = "rgba(255, 255, 255, 0.5)",
  disabled = false,
  ariaLabel,
  className,
  style,
  children,
  ...props
}) => {
  // Don't render spinner if not loading and no children
  if (!isLoading && !children) {
    return null;
  }

  // Show children when not loading
  if (!isLoading && children) {
    return <>{children}</>;
  }

  const spinnerProps = {
    testId: props.testId,
    size,
    color,
    speed,
    width,
    height,
    disabled,
  };

  const renderSpinner = () => {
    switch (variant) {
      case "pulse":
        return <PulseSpinner {...spinnerProps} />;

      case "bounce":
        return <BounceSpinner {...spinnerProps} />;

      case "ring":
        return <RingSpinner {...spinnerProps} />;

      case "fold":
        return <FoldSpinner {...spinnerProps} />;

      case "rotate":
      default:
        return <RotateSpinner {...spinnerProps} />;
    }
  };

  return (
    <SpinnerWrapper
      className={className}
      style={style}
      centered={centered}
      overlay={overlay}
      overlayMode={overlayMode}
      overlayColor={overlayColor}
      disabled={disabled}
      role="status"
      aria-label={ariaLabel ?? (showText ? text : "Loading")}
      aria-live="polite"
      {...props}
    >
      {renderSpinner()}
      {showText && (
        <LoadingText size={size} color={color}>
          {text}
        </LoadingText>
      )}
    </SpinnerWrapper>
  );
};
